import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaArrowRight, FaNewspaper } from 'react-icons/fa';
import PageHeader from '../components/common/PageHeader';

const PressReleasePage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Press Release' }
  ];

  const [activeFilter, setActiveFilter] = useState('all');

  const pressReleases = [
    {
      id: 1,
      title: "Segway Capital Launches New Blockchain Investment Fund",
      date: "January 15, 2023",
      excerpt: "Segway Capital announces the launch of a new blockchain investment fund focusing on innovative blockchain technologies and applications, with an initial capital commitment of $100 million.",
      category: "fund",
      image: "/assets/press/blockchain-fund.jpg"
    },
    {
      id: 2,
      title: "Segway Capital Expands Global Presence with New Office in Madrid",
      date: "March 8, 2023",
      excerpt: "Segway Capital continues its global expansion with the opening of a new office in Madrid, Spain, strengthening its presence in the European market and enhancing its ability to serve clients across the region.",
      category: "company",
      image: "/assets/press/madrid-office.jpg"
    },
    {
      id: 3,
      title: "Segway Capital Partners with Renewable Energy Leaders for New Fund",
      date: "April 22, 2023",
      excerpt: "Segway Capital announces a strategic partnership with leading renewable energy companies to launch a dedicated renewable energy investment fund, targeting sustainable projects across Europe and North America.",
      category: "fund",
      image: "/assets/press/renewable-energy.jpg"
    },
    {
      id: 4,
      title: "Segway Capital Achieves Record Growth in Q2 2023",
      date: "July 10, 2023",
      excerpt: "Segway Capital reports exceptional performance in Q2 2023, with assets under management reaching $5 billion, representing a 25% year-over-year growth and marking a significant milestone for the company.",
      category: "performance",
      image: "/assets/press/growth-chart.jpg"
    },
    {
      id: 5,
      title: "Segway Capital Launches Advanced Financial Advisory Service",
      date: "September 5, 2023",
      excerpt: "Segway Capital introduces a new comprehensive financial advisory service, leveraging AI technology and expert human advisors to provide personalized investment strategies for high-net-worth individuals.",
      category: "service",
      image: "/assets/press/advisory-service.jpg"
    },
    {
      id: 6,
      title: "Segway Capital Recognized as Top Blockchain Investment Firm",
      date: "November 20, 2023",
      excerpt: "Segway Capital has been named 'Top Blockchain Investment Firm of 2023' by Financial Innovation Awards, recognizing its innovative approach to blockchain investments and outstanding client services.",
      category: "award",
      image: "/assets/press/award.jpg"
    }
  ];

  const featuredPress = pressReleases[0];
  const regularPressReleases = pressReleases.slice(1);

  const filteredPressReleases = activeFilter === 'all' 
    ? regularPressReleases 
    : regularPressReleases.filter(item => item.category === activeFilter);

  const categories = [
    { id: 'all', label: 'All News' },
    { id: 'fund', label: 'Fund News' },
    { id: 'company', label: 'Company Updates' },
    { id: 'performance', label: 'Performance' },
    { id: 'service', label: 'Service Updates' },
    { id: 'award', label: 'Awards & Recognition' }
  ];

  return (
    <>
      <PageHeader 
        title="Press Release" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="relative rounded-xl overflow-hidden shadow-custom">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20 z-10"></div>
              <img 
                src={featuredPress.image || "/assets/press/default-featured.jpg"} 
                alt={featuredPress.title} 
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-8 z-20 text-white">
                <div className="flex items-center text-secondary mb-4">
                  <FaNewspaper className="mr-2" />
                  <span className="font-medium">Featured Announcement</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">{featuredPress.title}</h2>
                <div className="flex items-center text-gray-300 mb-4">
                  <FaCalendarAlt className="mr-2" />
                  <span>{featuredPress.date}</span>
                </div>
                <p className="text-lg text-gray-200 mb-6">{featuredPress.excerpt}</p>
                <a 
                  href={`/press-release/${featuredPress.id}`} 
                  className="inline-flex items-center text-secondary hover:text-secondary-light transition-colors font-medium"
                >
                  Read Full Press Release <FaArrowRight className="ml-2" />
                </a>
              </div>
            </div>
          </motion.div>
          
          <div className="mb-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="flex flex-wrap gap-3 mb-10"
            >
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`px-5 py-2 rounded-full transition-colors ${
                    activeFilter === category.id
                      ? 'bg-secondary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPressReleases.map((item, index) => (
                <motion.div 
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={item.image || "/assets/press/default.jpg"} 
                      alt={item.title} 
                      className="w-full h-full object-cover transition-transform hover:scale-105 duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-gray-500 mb-3">
                      <FaCalendarAlt className="mr-2 text-secondary" />
                      <span>{item.date}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-primary mb-3 line-clamp-2">{item.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{item.excerpt}</p>
                    <a 
                      href={`/press-release/${item.id}`} 
                      className="inline-flex items-center text-secondary hover:text-secondary-dark transition-colors font-medium"
                    >
                      Read More <FaArrowRight className="ml-2" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {filteredPressReleases.length > 0 ? (
              <button className="px-8 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors">
                Load More News
              </button>
            ) : (
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-2">No press releases found</h3>
                <p className="text-gray-600">No press releases match the selected category. Please try another category.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PressReleasePage; 